import { DataTable } from '@/components/DataTable';
import {
  Customer,
  CustomerBoolExp,
  CustomerOrderBy,
  OrderBy,
} from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import useDataTable from '@/hooks/useDataTable';
import { useSyncFiltersToSearchParams } from '@/hooks/useSyncFiltersToSearchParams';
import { SearchParam } from '@/models/search-param.models';
import { searchParamToTableSortingState } from '@/utils/search-param.function';
import { toSqlLikeString } from '@/utils/string';
import { useQuery } from '@tanstack/react-query';
import { PaginationState, SortingState } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { customerColumns } from '../components/CustomerColumns';
import ListCustomerHeader, {
  CustomerFilter,
} from '../components/ListCustomerHeader';
import { customerQuery } from '../graphql/query';

type CustomerQueryFilterInput = {
  where: CustomerBoolExp;
  limit: number;
  offset: number;
  orderBy: CustomerOrderBy[];
};

export default function ListCustomerPage() {
  const DEFAULT_PAGE_SIZE = 50;
  const DEFAULT_DATA_ORDER: CustomerOrderBy[] = [
    {
      createdAt: OrderBy.Desc,
    },
  ];
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [filterFormsData, setFilterFormsData] = useState<CustomerFilter>();

  const [filters, setFilters] = useState<CustomerQueryFilterInput>({
    limit: DEFAULT_PAGE_SIZE,
    offset: 0,
    orderBy: DEFAULT_DATA_ORDER,
    where: {},
  });

  const [searchParams, _setSearchParams] = useSearchParams();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  // This state indicate when if the search params are loaded after the page load.
  const [searchParamsLoaded, setSearchParamsLoaded] = useState<boolean>(false);

  const { data, isFetching, isLoading } = useQuery({
    queryKey: ['Customers', filters],
    queryFn: () =>
      execute({
        query: customerQuery,
        variables: {
          limit: filters.limit,
          offset: filters.offset,
          where: filters.where,
          orderBy: filters.orderBy,
        },
      }),
    select: (data) => data,
    staleTime: 3000,
    enabled: searchParamsLoaded,
  });

  useSyncFiltersToSearchParams<CustomerFilter>({
    filters: filterFormsData,
    pagination,
    sorting,
    setPagination,
    searchParamsLoaded,
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const table = useDataTable({
    data: customers,
    columns: customerColumns,
    rowCount,
    sorting,
    pagination,
    setSorting,
    setPagination,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
  });

  useEffect(() => {
    if (data) {
      setCustomers((data.customer ?? []) as Customer[]);
      setRowCount((data.customerAggregate?.aggregate?.count as number) ?? 0);
    }
  }, [data]);

  useEffect(() => {
    const name = searchParams.get(SearchParam.NAME);
    const pageSize = parseInt(
      searchParams.get(SearchParam.PAGE_SIZE) ?? DEFAULT_PAGE_SIZE.toString(),
    );
    const pageIndex = parseInt(searchParams.get(SearchParam.PAGE) ?? '0');
    const orderBy = searchParams.get(SearchParam.ORDER_BY);
    const limit = pageSize;
    const offset = pageIndex * limit;
    const sortingState = searchParamToTableSortingState(orderBy);

    // Populate form fields, table sorting, and pagination using values from the search parameters.
    if (!searchParamsLoaded) {
      setFilterFormsData({
        name,
      });
      setSorting(sortingState);
      setPagination({
        pageIndex,
        pageSize,
      });
    }
    // Create Site Where Filter from SearchParams.
    const andFilters: CustomerBoolExp[] = [];
    if (name) {
      andFilters.push({
        name: {
          _ilike: toSqlLikeString(name),
        },
      });
    }

    // Convert the react table SortingState to Site Query OrderBy
    const orderByFromParams = sortingState.map((sort): CustomerOrderBy => {
      return {
        [sort.id]: sort.desc ? OrderBy.DescNullsLast : OrderBy.AscNullsLast,
      };
    });

    setSearchParamsLoaded(true);
    setFilters({
      limit,
      offset,
      where: {
        _and: andFilters,
      },
      orderBy: orderByFromParams,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <>
      <ListCustomerHeader
        table={table}
        filters={filterFormsData}
        setFilters={setFilterFormsData}
      />
      <DataTable table={table} loading={isLoading} fetching={isFetching} />
    </>
  );
}
